import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Check, Users, UserPlus, Loader2, Download, Image, Share2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Position cycle options
const POSITION_CYCLE = ['GK', 'DEF', 'MID', 'FWD'];

// Position colors for visual distinction
const POSITION_COLORS = {
  'GK': 'bg-yellow-600 text-black',
  'DEF': 'bg-blue-600 text-white',
  'MID': 'bg-green-600 text-white',
  'FWD': 'bg-red-600 text-white',
};

// Common soccer formations
const FORMATIONS = {
  '4-4-2': { name: '4-4-2', positions: ['GK', 'RB', 'CB', 'CB', 'LB', 'RM', 'CM', 'CM', 'LM', 'ST', 'ST'] },
  '4-3-3': { name: '4-3-3', positions: ['GK', 'RB', 'CB', 'CB', 'LB', 'CM', 'CM', 'CM', 'RW', 'ST', 'LW'] },
  '4-2-3-1': { name: '4-2-3-1', positions: ['GK', 'RB', 'CB', 'CB', 'LB', 'CDM', 'CDM', 'RAM', 'CAM', 'LAM', 'ST'] },
  '3-5-2': { name: '3-5-2', positions: ['GK', 'CB', 'CB', 'CB', 'RM', 'CM', 'CM', 'CM', 'LM', 'ST', 'ST'] },
  '4-1-4-1': { name: '4-1-4-1', positions: ['GK', 'RB', 'CB', 'CB', 'LB', 'CDM', 'RM', 'CM', 'CM', 'LM', 'ST'] },
  '3-4-3': { name: '3-4-3', positions: ['GK', 'CB', 'CB', 'CB', 'RM', 'CM', 'CM', 'LM', 'RW', 'ST', 'LW'] },
  '5-3-2': { name: '5-3-2', positions: ['GK', 'RWB', 'CB', 'CB', 'CB', 'LWB', 'CM', 'CM', 'CM', 'ST', 'ST'] },
  '5-4-1': { name: '5-4-1', positions: ['GK', 'RWB', 'CB', 'CB', 'CB', 'LWB', 'RM', 'CM', 'CM', 'LM', 'ST'] },
};

const LineupSubmit = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [generatingGraphic, setGeneratingGraphic] = useState(null);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedStarting, setSelectedStarting] = useState([]);
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [managerName, setManagerName] = useState('');
  const [playerNumbers, setPlayerNumbers] = useState({});
  const [playerPositions, setPlayerPositions] = useState({});
  const [formation, setFormation] = useState('4-4-2');

  const generateSocialGraphic = async (graphicType) => {
    if (!data?.game_id || !data?.team_number) {
      toast.error('Missing game information');
      return;
    }
    
    setGeneratingGraphic(graphicType);
    try {
      const response = await axios.post(`${API}/api/graphics/generate-public?token=${token}`, {
        game_id: data.game_id,
        team_number: data.team_number,
        graphic_type: graphicType
      });
      
      // Download the generated graphic using fetch + blob
      const downloadUrl = `${API}${response.data.url}`;
      
      // Fetch the image as blob
      const imageResponse = await fetch(downloadUrl);
      const blob = await imageResponse.blob();
      
      // Create object URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success(`${graphicType.replace(/_/g, ' ')} graphic downloaded!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate graphic');
    } finally {
      setGeneratingGraphic(null);
    }
  };

  useEffect(() => {
    if (!token) {
      setError('Invalid lineup link - no token provided');
      setLoading(false);
      return;
    }
    
    fetchLineupData();
  }, [token]);

  const fetchLineupData = async () => {
    try {
      const response = await axios.get(`${API}/api/lineup/${token}`);
      setData(response.data);
      
      // Initialize player numbers and positions from squad
      const initialNumbers = {};
      const initialPositions = {};
      (response.data.players || []).forEach(p => {
        initialNumbers[p.id] = p.number;
        // Map existing positions to simplified format (GK, DEF, MID, FWD)
        const pos = (p.position || '').toUpperCase();
        if (pos === 'GK' || pos === 'GOALKEEPER') {
          initialPositions[p.id] = 'GK';
        } else if (['DEF', 'CB', 'RB', 'LB', 'FB', 'RWB', 'LWB', 'DEFENDER'].includes(pos)) {
          initialPositions[p.id] = 'DEF';
        } else if (['MID', 'CM', 'CDM', 'CAM', 'RM', 'LM', 'RAM', 'LAM', 'MIDFIELDER', 'HB', 'HF'].includes(pos)) {
          initialPositions[p.id] = 'MID';
        } else if (['FWD', 'ST', 'CF', 'RW', 'LW', 'FORWARD', 'STRIKER', 'FF'].includes(pos)) {
          initialPositions[p.id] = 'FWD';
        } else {
          initialPositions[p.id] = 'MID'; // Default to MID if unknown
        }
      });
      
      // Load existing selections and numbers if already submitted
      if (response.data.current_lineup?.starting) {
        setSelectedStarting(response.data.current_lineup.starting.map(p => p.id));
        // Override with submitted numbers and positions
        response.data.current_lineup.starting.forEach(p => {
          initialNumbers[p.id] = p.number;
          if (p.position) {
            const pos = p.position.toUpperCase();
            if (POSITION_CYCLE.includes(pos)) {
              initialPositions[p.id] = pos;
            }
          }
        });
      }
      if (response.data.current_lineup?.substitutes) {
        setSelectedSubs(response.data.current_lineup.substitutes.map(p => p.id));
        response.data.current_lineup.substitutes.forEach(p => {
          initialNumbers[p.id] = p.number;
          if (p.position) {
            const pos = p.position.toUpperCase();
            if (POSITION_CYCLE.includes(pos)) {
              initialPositions[p.id] = pos;
            }
          }
        });
      }
      if (response.data.current_lineup?.manager_name) {
        setManagerName(response.data.current_lineup.manager_name);
      }
      if (response.data.current_lineup?.formation) {
        setFormation(response.data.current_lineup.formation);
      }
      
      setPlayerNumbers(initialNumbers);
      setPlayerPositions(initialPositions);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load lineup data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleNumberChange = (playerId, newNumber) => {
    setPlayerNumbers(prev => ({
      ...prev,
      [playerId]: newNumber === '' ? '' : parseInt(newNumber) || prev[playerId]
    }));
  };

  const togglePosition = (playerId) => {
    setPlayerPositions(prev => {
      const currentPos = prev[playerId] || 'MID';
      const currentIndex = POSITION_CYCLE.indexOf(currentPos);
      const nextIndex = (currentIndex + 1) % POSITION_CYCLE.length;
      return {
        ...prev,
        [playerId]: POSITION_CYCLE[nextIndex]
      };
    });
  };

  const togglePlayer = (playerId, listType) => {
    if (listType === 'starting') {
      if (selectedStarting.includes(playerId)) {
        setSelectedStarting(selectedStarting.filter(id => id !== playerId));
      } else {
        // Remove from subs if adding to starting
        setSelectedSubs(selectedSubs.filter(id => id !== playerId));
        setSelectedStarting([...selectedStarting, playerId]);
      }
    } else {
      if (selectedSubs.includes(playerId)) {
        setSelectedSubs(selectedSubs.filter(id => id !== playerId));
      } else {
        // Remove from starting if adding to subs
        setSelectedStarting(selectedStarting.filter(id => id !== playerId));
        setSelectedSubs([...selectedSubs, playerId]);
      }
    }
  };

  const handleSubmit = async () => {
    const requiredStarters = data.sport === 'soccer' ? 11 : 15;
    
    if (selectedStarting.length < requiredStarters) {
      toast.error(`Please select ${requiredStarters} starting players`);
      return;
    }
    
    setSubmitting(true);
    try {
      // Include custom numbers, positions, and formation with the submission
      await axios.post(`${API}/api/lineup/${token}/submit`, {
        starting: selectedStarting,
        substitutes: selectedSubs,
        manager_name: managerName,
        player_numbers: playerNumbers,
        player_positions: playerPositions,
        formation: formation
      });
      
      toast.success('Lineup submitted successfully!');
      fetchLineupData(); // Refresh to show submitted state
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit lineup');
    } finally {
      setSubmitting(false);
    }
  };

  const getPositionOrder = (position) => {
    const order = { 'GK': 1, 'DEF': 2, 'CB': 2, 'FB': 2, 'HB': 3, 'MID': 3, 'HF': 4, 'FWD': 5, 'FF': 5 };
    return order[position] || 99;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#3B82F6] animate-spin mx-auto mb-4" />
          <p className="text-white font-body">Loading lineup...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4">
        <Card className="bg-[#1E293B] border-red-500/50 max-w-md w-full">
          <CardContent className="py-8 text-center">
            <p className="text-red-400 font-body text-lg">{error}</p>
            <p className="text-slate-500 font-body text-sm mt-2">
              Please check the link and try again
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const requiredStarters = data.sport === 'soccer' ? 11 : 15;
  const sortedPlayers = [...(data.players || [])].sort((a, b) => {
    const posOrder = getPositionOrder(a.position) - getPositionOrder(b.position);
    if (posOrder !== 0) return posOrder;
    return a.number - b.number;
  });

  return (
    <div className="min-h-screen bg-[#0F172A] p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <span className="text-xs font-body uppercase tracking-widest text-[#D4E5ED] bg-[#2A4A5C] px-3 py-1 rounded">
            LINEUP SUBMISSION
          </span>
          <h1 className="font-heading text-3xl text-white mt-3">{data.team_name}</h1>
          <p className="text-slate-400 font-body mt-1">
            vs {data.opponent} • {data.competition || 'Match'}
          </p>
        </div>

        {data.submitted && (
          <Card className="bg-green-900/30 border-green-500/50 mb-6">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Check className="w-6 h-6 text-green-400" />
                <div>
                  <p className="text-green-400 font-body font-semibold">Lineup Submitted!</p>
                  <p className="text-green-300/70 font-body text-sm">
                    You can update your selections until the match begins
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Manager Name */}
        <Card className="bg-[#1E293B] border-slate-700 mb-4">
          <CardContent className="py-4">
            <Label className="text-slate-300 font-body">Manager / Coach Name</Label>
            <Input
              value={managerName}
              onChange={(e) => setManagerName(e.target.value)}
              placeholder="Enter manager name"
              className="bg-[#0F172A] border-slate-600 text-white font-body mt-2"
            />
          </CardContent>
        </Card>

        {/* Formation Selection (Soccer only) */}
        {data.sport === 'soccer' && (
          <Card className="bg-[#1E293B] border-slate-700 mb-4">
            <CardContent className="py-4">
              <Label className="text-slate-300 font-body">Formation</Label>
              <Select value={formation} onValueChange={setFormation}>
                <SelectTrigger className="bg-[#0F172A] border-slate-600 text-white mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-slate-600">
                  {Object.keys(FORMATIONS).map(f => (
                    <SelectItem key={f} value={f}>{FORMATIONS[f].name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {/* Selection Summary */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <Card className="bg-[#1E293B] border-slate-700">
            <CardContent className="py-4 text-center">
              <p className="text-3xl font-heading text-white">{selectedStarting.length}/{requiredStarters}</p>
              <p className="text-slate-400 font-body text-sm">Starting</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1E293B] border-slate-700">
            <CardContent className="py-4 text-center">
              <p className="text-3xl font-heading text-white">{selectedSubs.length}</p>
              <p className="text-slate-400 font-body text-sm">Substitutes</p>
            </CardContent>
          </Card>
        </div>

        {/* Instructions */}
        <Card className="bg-[#2A4A5C]/30 border-[#2A4A5C] mb-4">
          <CardContent className="py-3">
            <p className="text-[#D4E5ED] font-body text-sm text-center">
              Tap a player once for <span className="text-green-400 font-semibold">Starting XI</span>, 
              tap again for <span className="text-yellow-400 font-semibold">Substitute</span>, 
              tap again to <span className="text-slate-400">deselect</span>.
            </p>
            <p className="text-[#D4E5ED] font-body text-sm text-center mt-2">
              <span className="text-slate-400">Tap the number to edit • Tap position button to cycle: </span>
              <span className="text-yellow-400">GK</span> → 
              <span className="text-blue-400">DEF</span> → 
              <span className="text-green-400">MID</span> → 
              <span className="text-red-400">FWD</span>
            </p>
          </CardContent>
        </Card>

        {/* Player List */}
        <Card className="bg-[#1E293B] border-slate-700 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-white font-heading flex items-center gap-2">
              <Users className="w-5 h-5" />
              Squad ({data.players?.length || 0} players)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {sortedPlayers.length === 0 ? (
              <p className="text-slate-500 font-body text-center py-4">No players in squad</p>
            ) : (
              sortedPlayers.map((player) => {
                const isStarting = selectedStarting.includes(player.id);
                const isSub = selectedSubs.includes(player.id);
                const currentNumber = playerNumbers[player.id] ?? player.number;
                const currentPosition = playerPositions[player.id] || 'MID';
                
                return (
                  <div
                    key={player.id}
                    className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                      isStarting 
                        ? 'bg-green-600/30 border-2 border-green-500' 
                        : isSub 
                          ? 'bg-yellow-600/20 border-2 border-yellow-500' 
                          : 'bg-[#0F172A] border-2 border-transparent hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {/* Editable Number */}
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={currentNumber}
                        onChange={(e) => handleNumberChange(player.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className={`w-10 h-10 rounded-full text-center font-bold text-sm border-0 focus:ring-2 focus:ring-blue-500 ${
                          isStarting 
                            ? 'bg-green-600 text-white' 
                            : isSub 
                              ? 'bg-yellow-600 text-white' 
                              : 'bg-slate-700 text-white'
                        }`}
                      />
                      
                      {/* Position Toggle Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePosition(player.id);
                        }}
                        className={`w-12 h-8 rounded text-xs font-bold transition-colors ${POSITION_COLORS[currentPosition]}`}
                        title="Tap to change position"
                      >
                        {currentPosition}
                      </button>
                      
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => {
                          if (!isStarting && !isSub) {
                            togglePlayer(player.id, 'starting');
                          } else if (isStarting) {
                            togglePlayer(player.id, 'substitute');
                          } else {
                            togglePlayer(player.id, 'starting');
                            setSelectedSubs(selectedSubs.filter(id => id !== player.id));
                            setSelectedStarting(selectedStarting.filter(id => id !== player.id));
                          }
                        }}
                      >
                        <p className="text-white font-body font-medium">{player.name}</p>
                      </div>
                    </div>
                    <div 
                      className="text-right cursor-pointer pl-4"
                      onClick={() => {
                        if (!isStarting && !isSub) {
                          togglePlayer(player.id, 'starting');
                        } else if (isStarting) {
                          togglePlayer(player.id, 'substitute');
                        } else {
                          togglePlayer(player.id, 'starting');
                          setSelectedSubs(selectedSubs.filter(id => id !== player.id));
                          setSelectedStarting(selectedStarting.filter(id => id !== player.id));
                        }
                      }}
                    >
                      {isStarting && (
                        <span className="text-green-400 font-body text-sm font-semibold">STARTING</span>
                      )}
                      {isSub && (
                        <span className="text-yellow-400 font-body text-sm font-semibold">SUB</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={submitting || selectedStarting.length < requiredStarters}
          className="w-full h-14 bg-[#22C55E] hover:bg-[#16A34A] text-white font-body text-lg disabled:opacity-50"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Submitting...
            </>
          ) : data.submitted ? (
            'Update Lineup'
          ) : (
            'Submit Lineup'
          )}
        </Button>

        {selectedStarting.length < requiredStarters && (
          <p className="text-center text-slate-500 font-body text-sm mt-2">
            Select {requiredStarters - selectedStarting.length} more starting player{requiredStarters - selectedStarting.length !== 1 ? 's' : ''}
          </p>
        )}

        {/* Social Media Graphics - Show after lineup is submitted */}
        {data.submitted && (
          <Card className="bg-[#1E293B] border-slate-700 mt-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-white font-body text-sm flex items-center gap-2">
                <Share2 className="w-4 h-4" />
                Share on Social Media
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-400 font-body text-xs">
                Download graphics to share your lineup on social media
              </p>
              
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => generateSocialGraphic('lineup_post')}
                  disabled={generatingGraphic === 'lineup_post'}
                  className="h-12 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white text-sm"
                >
                  <Image className="w-4 h-4 mr-2" />
                  {generatingGraphic === 'lineup_post' ? 'Generating...' : 'Lineup Post'}
                </Button>
                <Button
                  onClick={() => generateSocialGraphic('lineup_story')}
                  disabled={generatingGraphic === 'lineup_story'}
                  className="h-12 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white text-sm"
                >
                  <Image className="w-4 h-4 mr-2" />
                  {generatingGraphic === 'lineup_story' ? 'Generating...' : 'Lineup Story'}
                </Button>
                <Button
                  onClick={() => generateSocialGraphic('matchday_post')}
                  disabled={generatingGraphic === 'matchday_post'}
                  className="h-12 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white text-sm"
                >
                  <Image className="w-4 h-4 mr-2" />
                  {generatingGraphic === 'matchday_post' ? 'Generating...' : 'Matchday Post'}
                </Button>
                <Button
                  onClick={() => generateSocialGraphic('matchday_story')}
                  disabled={generatingGraphic === 'matchday_story'}
                  className="h-12 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white text-sm"
                >
                  <Image className="w-4 h-4 mr-2" />
                  {generatingGraphic === 'matchday_story' ? 'Generating...' : 'Matchday Story'}
                </Button>
              </div>
              
              <p className="text-slate-500 font-body text-xs text-center">
                Post = 1000×1000 • Story = 1080×1920
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LineupSubmit;
